/**
 * The slash-command palette — RN counterpart of the web widget's
 * `slash-command-menu.tsx`. It sits inside the composer pill, above the field,
 * in the same single container web uses.
 *
 * Two web affordances are gone rather than faked. Arrow-key navigation has no
 * equivalent on a soft keyboard, which also removes the reason for web's
 * scroll-the-active-row-into-view effect: `activeIndex` can now only move to a
 * row the user can already see and touch. And hover, the other thing that moved
 * it, becomes the tap that selects the row.
 *
 * A tap has to land while the keyboard is up — that is the only state this menu
 * is ever visible in — so `keyboardShouldPersistTaps` is load-bearing: without
 * it the first tap is spent dismissing the keyboard.
 */

import type { FC } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { Command } from "lucide-react-native";

import { solidColor } from "../lib/gradient";
import type { SlashCommand } from "../lib/slash-command-types";
import { ringgId } from "../lib/testing";
import { resolveFontFamily } from "../lib/widget-theme";
import { Touchable } from "./ui/pressable";

/** Web's inline palette plus the family: RN text inherits nothing from above. */
interface SlashCommandMenuTheme {
  backgroundColor: string;
  surfaceColor: string;
  borderColor: string;
  textColor: string;
  mutedTextColor: string;
  primaryColor: string;
  fontFamily: string;
}

interface SlashCommandMenuProps {
  commands: SlashCommand[];
  activeIndex: number;
  theme: SlashCommandMenuTheme;
  onSelect: (command: SlashCommand) => void;
  onActiveIndexChange: (index: number) => void;
}

/** `max-h-[180px]` — roughly four rows before the list scrolls. */
const LIST_MAX_HEIGHT = 180;

/** Hex alpha on the active row's icon tile, web's `+ "18"` (~9%). */
const ACTIVE_ICON_ALPHA = "18";

const SIX_DIGIT_HEX = /^#[0-9a-f]{6}$/i;

/**
 * The active tile is the primary colour at a whisper of opacity, which web
 * expresses by suffixing hex alpha. RN rejects a colour string it cannot parse
 * instead of ignoring it, so a primary that is not six-digit hex (`rgb(...)`, a
 * named colour, a gradient reduced to either) keeps the flat surface.
 */
const iconBackground = (theme: SlashCommandMenuTheme, isActive: boolean): string => {
  if (!isActive) return theme.surfaceColor;
  const primary = solidColor(theme.primaryColor);
  return SIX_DIGIT_HEX.test(primary) ? `${primary}${ACTIVE_ICON_ALPHA}` : theme.surfaceColor;
};

const SlashCommandMenu: FC<SlashCommandMenuProps> = ({ commands, activeIndex, theme, onSelect, onActiveIndexChange }) => {
  if (commands.length === 0) return null;

  const fontFamily = resolveFontFamily(theme.fontFamily);
  const primaryColor = solidColor(theme.primaryColor);

  return (
    <View testID={ringgId("slash-command-menu")}>
      <View testID={ringgId("slash-command-header")} style={[styles.header, { borderBottomColor: solidColor(theme.borderColor) }]}>
        <Text testID={ringgId("slash-command-heading")} style={[styles.heading, { color: theme.mutedTextColor, fontFamily }]}>
          Commands
        </Text>
      </View>
      <ScrollView testID={ringgId("slash-command-list")} style={styles.list} contentContainerStyle={styles.listContent} keyboardShouldPersistTaps="handled">
        {commands.map((cmd, index) => {
          const isActive = index === activeIndex;
          return (
            <Touchable
              key={cmd.command}
              testID={ringgId("slash-command-item")}
              // Web also marks the row active on hover; here the tap that
              // selects it is the only pointer event there is. The fill is the
              // whole of that signal — a testID carries one value, so web's
              // `data-ringg-active` has nowhere to go.
              onPress={() => {
                onActiveIndexChange(index);
                onSelect(cmd);
              }}
              // Rows are stacked and already tall enough to hit; the default
              // touch expansion would only overlap their neighbours.
              hitSlop={0}
              style={[styles.item, { backgroundColor: isActive ? theme.surfaceColor : "transparent" }]}
            >
              <View testID={ringgId("slash-command-icon")} style={[styles.icon, { backgroundColor: iconBackground(theme, isActive) }]}>
                <Command size={12} color={primaryColor} />
              </View>
              <View testID={ringgId("slash-command-text")} style={styles.text}>
                <Text testID={ringgId("slash-command-name")} style={[styles.name, { color: theme.textColor, fontFamily }]}>
                  {cmd.display_name}
                </Text>
                {/* Web hangs the untruncated description off `title`; the
                    accessibility label is where text the eye cannot finish
                    reading lives on this platform. */}
                <Text testID={ringgId("slash-command-description")} style={[styles.description, { color: theme.mutedTextColor, fontFamily }]} numberOfLines={1} accessibilityLabel={cmd.description}>
                  {cmd.description}
                </Text>
              </View>
            </Touchable>
          );
        })}
      </ScrollView>
    </View>
  );
};

export default SlashCommandMenu;

const styles = StyleSheet.create({
  header: { paddingHorizontal: 12, paddingVertical: 8, borderBottomWidth: StyleSheet.hairlineWidth },
  heading: { fontSize: 10, fontWeight: "500", textTransform: "uppercase", letterSpacing: 0.5 },
  list: { maxHeight: LIST_MAX_HEIGHT },
  listContent: { paddingVertical: 4 },
  item: { flexDirection: "row", alignItems: "flex-start", gap: 10, paddingHorizontal: 12, paddingVertical: 8 },
  icon: { marginTop: 2, flexShrink: 0, width: 24, height: 24, borderRadius: 6, alignItems: "center", justifyContent: "center" },
  text: { flex: 1 },
  name: { fontSize: 12, lineHeight: 16, fontWeight: "500" },
  description: { fontSize: 10 },
});
